import { el, clear, toast } from '../dom';
import { setLeaveGuard, clearLeaveGuard } from '../leave-guard';
import { arenaBrApi, type BrStatus } from '../game-api';
import { api, ApiError } from '../../core/network/api';
import type { GameCtx, ViewTeardown } from '../types';
import { renderArenaMatch } from './arena-match';
import { generateRoster, soloSeats, type MatchSeat } from '../arena/names';
import { dispatchAction, isDropped } from '../action/dispatch';
import {
  readMatchSnapshot, writeMatchSnapshot, clearMatchSnapshot, matchResumeAction, type MatchSnapshot,
} from '../arena/resume';

/**
 * Arena entry point (Block 11) with §6.5 matchmaking.
 *
 * Flow: lobby → SEARCH (opponents "connect" over ~a search window) → OFFER (a
 * "Start Battle" button with a 30s accept countdown) → match. Ignoring the offer
 * for 30s (or declining) removes you from the queue and bans you from search for the
 * backend's ARENA_BR.SEARCH_BAN_MS — 3 minutes (arena-br.config.ts; it was 30, far too
 * punishing for an accidental miss). The ban is stored server-side, so a reload can't
 * dodge it, and the lobby button shows a live countdown while it's active.
 *
 * Entry itself is still server-gated: 1 free play/day, then 100 coins per extra, up
 * to 10 plays a day. Beyond that a player may buy up to 10 more entries with Shrooms
 * (20/day ceiling). Banked gold is match SCORE only — it is never paid out as coins.
 */

// Matchmaking wait (owner: 1–3 minutes). Opponents "connect" one-by-one across this
// window so it reads like a real queue filling; Cancel is free (no ban) if you don't
// want to wait. Tune here if the queue should feel faster/slower.
const SEARCH_MIN_MS = 60_000;
const SEARCH_MAX_MS = 180_000;

/**
 * The server-side gather lobby (backend/src/game/arena/arena-lobby.service.ts).
 *
 * Declared here rather than in game-api.ts because the arena is the only caller and
 * the shapes are the arena's own. Everyone searching inside the same window lands in
 * ONE field: real players are seated first and bots take whatever slots are left —
 * which is what the FAQ's "real players take those places as they join" describes.
 */
interface LobbyView {
  id: string;
  seed: number;
  fieldSize: number;
  closesAt: number;
  ready: boolean;
  humans: number;
  bots: number;
  slot: number;
  seats: MatchSeat[];
}
const lobbyApi = {
  join: () => api.post<LobbyView>('/arena/br/lobby/join', {}),
  state: () => api.get<LobbyView>('/arena/br/lobby'),
  leave: () => api.post<LobbyView | null>('/arena/br/lobby/leave', {}),
};

/**
 * Refresh-proof search state (defect 19).
 *
 * All of search()'s state used to live in its closure, and renderArena's last act was
 * an unconditional `lobby()` — so a reload mid-queue dropped the player back to the
 * lobby screen while the server happily kept their seat (join is idempotent per user,
 * arena-lobby.service.ts). The seat was still there; nothing ever asked. We mirror the
 * bare minimum in sessionStorage (which lobby, which phase) and re-derive everything
 * else from the server on mount — the client never trusts its own copy of the queue.
 *
 * sessionStorage rather than localStorage on purpose: a queue belongs to the tab that
 * is waiting in it, and it must not leak into a second tab or survive the browser.
 */
export interface SearchResume {
  lobbyId: string;
  seed: number;
  /** Both phases are resumable; an OFFER additionally carries its live deadline. */
  phase: 'search' | 'offer';
  /** Wall-clock ms at which the accept window shuts (offer only). */
  offerEndsAt?: number;
  /** The FROZEN seating the offer was made for (offer only) — see the note below. */
  seats?: MatchSeat[];
}
export type ResumeAction = 'none' | 'resume-search' | 'resume-offer' | 'expired-offer' | 'expired';

/**
 * Don't hand back a window nobody can press.
 *
 * Restoring an offer with a second left is worse than not restoring it: the ring
 * would run out before the player finished reading it, and the timeout path DOES
 * decline — which is the 3-minute ban. Under this much remaining the offer is
 * declared expired instead, and expiry never declines.
 */
export const MIN_OFFER_RESTORE_MS = 5_000;

const RESUME_KEY = 'tb.arena.search';

/** Storage can throw outright (private mode, blocked site data) — never on our path. */
export function readSearchResume(): SearchResume | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<SearchResume>;
    if (typeof v?.lobbyId !== 'string' || !v.lobbyId) return null;
    if (v.phase !== 'search' && v.phase !== 'offer') return null;
    const out: SearchResume = { lobbyId: v.lobbyId, seed: Number(v.seed) >>> 0, phase: v.phase };
    if (Number.isFinite(v.offerEndsAt)) out.offerEndsAt = Number(v.offerEndsAt);
    // The seating is carried, never re-fetched: by the time an offer is made the
    // lobby has closed, and asking the server again returns the NEXT lobby — the
    // match would come back with the wrong opponents in it.
    if (Array.isArray(v.seats)) out.seats = v.seats as MatchSeat[];
    return out;
  } catch { return null; }
}
export function writeSearchResume(v: SearchResume): void {
  try { sessionStorage.setItem(RESUME_KEY, JSON.stringify(v)); } catch { /* nothing to lose */ }
}
export function clearSearchResume(): void {
  try { sessionStorage.removeItem(RESUME_KEY); } catch { /* nothing to lose */ }
}

/**
 * What a fresh mount should do about a search that was in flight.
 *
 * Pure so it can be tested without a DOM or a server: the whole point of the defect is
 * a decision nobody was making, and the decision is the part worth pinning down.
 *
 * Owner call (R2, reversing an earlier decision): a SEARCH is restored and an OFFER is
 * RESTORED TOO, with whatever is left of its deadline. The old behaviour released the
 * seat and bounced the player to the lobby, which threw away a match they were about
 * to accept for the crime of pressing F5. What must NEVER happen either way is a
 * decline: `POST /arena/br/decline` is the only route that sets searchBanUntil, and a
 * reload is not a refusal. An offer whose window has already run out (or has too
 * little left to press) is `expired-offer` — the seat goes back, and nothing else.
 *
 * Anything the server disagrees with about a SEARCH (lobby replaced, seat gone, window
 * shut, or the field already filled and moved on to its offer) is `expired`: say so,
 * don't silently pretend nothing happened.
 */
export function resumeAction(
  saved: SearchResume | null,
  st: { id: string; slot: number; ready: boolean; closesAt: number } | null,
  now: number,
): ResumeAction {
  if (!saved) return 'none';
  if (saved.phase === 'offer') {
    const endsAt = saved.offerEndsAt ?? 0;
    return endsAt - now >= MIN_OFFER_RESTORE_MS ? 'resume-offer' : 'expired-offer';
  }
  if (!st || st.id !== saved.lobbyId || st.slot < 0 || st.ready || now >= st.closesAt) return 'expired';
  return 'resume-search';
}

/** How many real players are in a seating (for the "N real players" line). */
const humansIn = (seats: readonly MatchSeat[]) => seats.filter((s) => s.kind === 'human').length;

const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * When the daily entry budget refills: the next UTC midnight. The server resets the
 * counters lazily off the UTC day (ArenaBrService.resetAt), so computing it here from
 * the same rule needs no extra status field and cannot disagree with the backend.
 */
const resetsIn = (now = Date.now()) => {
  const d = new Date(now);
  const at = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
  const mins = Math.ceil((at - now) / 60_000);
  if (mins < 1) return 'under a minute';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

// Pixel-art icons (sliced from the Cute Fantasy pack) that replace emoji in the
// arena UI. `icon()` renders one inline, crisp, baseline-aligned with the text.
const ICON = {
  coin: '/sprites/ui/currency/coin.png',
  shroom: '/sprites/ui/currency/shroom.png',
  swords: '/sprites/ui/arena/swords.png',
  star: '/sprites/ui/arena/star.png',
  check: '/sprites/ui/arena/check.png',
  shield: '/sprites/ui/icons/shield.png',
};
const icon = (src: string, h = 14) =>
  el('img', 'inline-block align-[-0.18em] mx-[1px]', { src, alt: '', style: `height:${h}px;width:auto;image-rendering:pixelated` });

/** One "how it works" line. The icon column is fixed-width whether or not the rule
 *  has a sprite, so the sentences all start on the same x — a ragged left edge is
 *  what an emoji-per-line list looked like once half the emoji were dropped. */
const rule = (art: HTMLElement | null, text: string) =>
  el('div', 'flex items-start gap-1.5', {},
    el('span', 'w-[15px] shrink-0 text-center', {}, art ?? ''),
    el('span', '', {}, text));

// Arena runs its own music bed; the farm restores day/night on return.
export function renderArena(root: HTMLElement, ctx: GameCtx): ViewTeardown {
  clear(root);
  let teardown: ViewTeardown | null = null;   // active match teardown, if mounted
  let busy = false;
  // The accept window is the SERVER's (ARENA_BR.ACCEPT_WINDOW_MS) and it is already on
  // the status payload. It used to be a hardcoded 30_000 in offer(), which was fine
  // while nothing depended on it — but a restored offer is restored against this
  // number, so a drift between client and server would hand back the wrong deadline.
  let acceptWindowMs = 30_000;
  const timers = new Set<number>();
  const every = (ms: number, fn: () => void) => { const id = window.setInterval(fn, ms); timers.add(id); return id; };
  const clearTimers = () => { for (const id of timers) window.clearInterval(id); timers.clear(); };
  // Every route out of the search (cancel, offer, lobby reload, view teardown) goes
  // through one of these, so the "you will forfeit your entry" prompt can never
  // outlive the search that armed it.
  const endSearchGuard = () => clearLeaveGuard();

  // ONE SCREEN: the scene never scrolls. It used to be `overflow-y-auto`, so a card
  // taller than the viewport simply pushed a scrollbar and the arena became a page
  // you had to travel down — on a laptop the entry buttons sat below the fold.
  // Now the scene is fixed to its box and the CARD is capped to fit inside it, with
  // any overlong section scrolling within the card instead of moving the whole view.
  const host = el('div', 'arena-scene w-full h-full overflow-hidden grid place-items-center p-3');
  root.append(host);

  // A lava-obsidian signboard panel wrapping the given content, centred.
  const panel = (...children: (Node | string)[]) =>
    el('div', 'arena-panel arena-font w-[min(430px,92vw)] max-h-full overflow-hidden flex flex-col text-center', {},
      el('div', 'arena-panel-inner flex flex-col items-center min-h-0 overflow-y-auto', {}, ...children));

  // ─── Lobby ──────────────────────────────────────────────────────────────────
  async function lobby() {
    teardown?.(); teardown = null; busy = false;
    clearTimers();
    endSearchGuard();       // back on the lobby screen: there is no search to lose
    clear(host);
    let st: BrStatus | null = null;
    // An outage must NOT be dressed up as a player problem: with `st` null the entry
    // gate below has no idea what you can afford, and it used to render "Need 100" —
    // telling a solvent player they were broke. `offline` says what actually happened
    // and offers a retry; entry itself stays server-gated either way.
    let offline = false;
    try { st = await arenaBrApi.status(); } catch { offline = true; }
    if (st?.acceptWindowMs) acceptWindowMs = st.acceptWindowMs;

    const banUntil = st?.searchBanUntil ? Date.parse(st.searchBanUntil) : 0;
    const free = !!st?.freeAvailable;
    const cost = st?.nextEntryCostCoins ?? 100;
    const coins = st?.coins ?? 0;
    // Daily budget: base 10 plays (1 of them free) + however many were bought today.
    const used = st?.entriesToday ?? 0;
    const limit = st?.entriesLimit ?? 10;
    const left = st?.entriesLeft ?? Math.max(0, limit - used);
    const outOfEntries = !!st && left <= 0;
    const boughtLeft = st?.boughtLeft ?? 0;
    // Spending the base allowance ("10/10") is NOT the same as being out of entries:
    // Shrooms still buy up to 10 more. Only when nothing is buyable either is the day
    // actually over, and only then must "buy more" stop being offered.
    const atCeiling = outOfEntries && boughtLeft <= 0;
    const entryShrooms = st?.entryCostShrooms ?? 15;
    const canAfford = !outOfEntries && (free || coins >= cost);
    // Cost line with inline coin/check icons instead of emoji.
    const costEl = el('span', 'inline-flex items-center gap-0.5 flex-wrap justify-center');
    if (!st) costEl.append('1 free/day · ', icon(ICON.coin), ' 100 per extra');
    else if (free) costEl.append(icon(ICON.check), ' Your free daily entry is ready');
    else costEl.append('Extra entry · ', icon(ICON.coin), ` ${cost} (you have `, icon(ICON.coin), ` ${coins})`);

    // THE GAP: this line used to reserve `min-h-[16px] mb-3` unconditionally, so in the
    // common case — free entry ready, nothing wrong — 28px of empty space sat between
    // the entry status and the button and read as a layout bug. It is now zero-height
    // until it actually has something to say; the margin comes with the message.
    const err = el('div', 'text-[11px] text-error font-mono flex items-center justify-center gap-0.5', {});
    if (offline) {
      err.replaceChildren("Couldn't reach the arena server — your entries and coins are unknown.");
    } else if (outOfEntries) {
      // Worded IDENTICALLY to ArenaBrService.exhaustedMessage — if the player pushes
      // past the panel somehow, the server's refusal must read like the same game.
      err.replaceChildren(atCeiling
        ? `All ${limit} arena entries for today are used — no more can be bought. Entries reset in ${resetsIn()}.`
        : `Daily arena entries used (${used}/${limit}) — buy more entries with Shrooms, or come back when they reset in ${resetsIn()}.`);
    } else if (!canAfford) {
      err.append('Not enough coins — you need ', icon(ICON.coin), ` ${cost}.`);
    }
    if (err.childNodes.length) err.classList.add('mb-2', 'min-h-[15px]');
    const btn = el('button',
      'arena-btn px-6 py-1.5 text-base inline-flex items-center gap-1.5',
      {}, icon(ICON.swords, 18), 'Find Match') as HTMLButtonElement;
    // Offline: the same button re-runs the lobby load instead of starting a search
    // that the server can't gate anyway. One listener, chosen here.
    btn.addEventListener('click', () => { if (offline) void lobby(); else void search(); });

    // Gate entry BEFORE the search so a broke player isn't led through the whole
    // search+offer only to be rejected at Start Battle.
    if (offline) { btn.replaceChildren('Arena unavailable — Retry'); }
    else if (atCeiling) { btn.disabled = true; btn.replaceChildren('No entries left today'); }
    else if (outOfEntries) { btn.disabled = true; btn.replaceChildren('Out of entries — buy more below'); }
    else if (!canAfford) { btn.disabled = true; btn.replaceChildren(icon(ICON.coin, 16), `Need ${cost}`); }

    // A live search ban disables the button and counts down on it.
    const refreshBan = () => {
      const remain = banUntil - Date.now();
      if (remain > 0) { btn.disabled = true; btn.textContent = `Search banned · ${mmss(remain)}`; }
      else if (canAfford) { btn.disabled = false; btn.replaceChildren(icon(ICON.swords, 18), 'Find Match'); clearTimers(); }
    };
    if (banUntil > Date.now()) { refreshBan(); every(1000, refreshBan); }

    host.append(panel(
      el('div', 'text-[20px] leading-none tracking-wide text-[#ffb648] mb-1', { style: 'text-shadow: 0 2px 0 #7a1f0a' }, 'LAVA ARENA'),
      // Entries used vs the allowance, on ONE wrapping line so the panel keeps fitting
      // its box: "today 3/10 played" never said what the 10 was, so a player at 10/10
      // with 10 buyable entries left read it as the end of the day. The bought entries
      // are broken out only once there are any, and the reset time is always shown.
      st ? el('div', 'text-[11px] font-mono text-[#e7b58a] mb-1.5', {},
        `Rating ${st.rating} · ${st.wins}W-${st.losses}L · entries ${used}/${limit}`
        + (st.entriesBought > 0 ? ` (${st.baseLimit} daily + ${st.entriesBought} bought)` : '')
        + ` · resets in ${resetsIn()}`) : el('span'),
      el('p', 'text-[11px] leading-snug text-[#e9d6bd]/90 mb-2 px-1', {},
        '25 heroes enter a lava-and-mushroom battleground. Bank your gold in the safe space before you die — dying with unbanked gold loses half. Last one standing takes the crown.'),
      // A TEASER, not the rulebook. This box used to carry five rules — safe-zone
      // heal, closing time, camping, banking, spectating — and every one of them is
      // now taught in the match itself by the first-match guide (tutorial/arena-tour.ts),
      // pointed at the real sanctuary and the real HUD. Two copies of the same rules
      // is two copies to keep true, and this one has no way of knowing when the
      // numbers move. So it says only what a player needs to decide to press ENTER.
      el('div', 'text-left text-[11px] leading-snug text-[#e9d6bd]/85 bg-black/25 border border-[#7a3a12]/60 rounded-lg px-2.5 py-2 mb-2.5 space-y-0.5', {},
        el('div', 'text-[#ffb648] font-mono text-[10px] tracking-wide mb-0.5', {}, 'HOW IT WORKS'),
        // Pixel icons where the pack HAS one (shield for the sanctuary, the coin for
        // banking); the rest lead with a capitalised keyword instead. An unmapped
        // emoji here was worse than no icon: richText drops it, and these lines were
        // raw children, so the hourglass/no-entry/eye rendered as system glyphs — the
        // three shapes on the panel that were not pixel art.
        rule(icon(ICON.shield), 'A sanctuary at the centre banks your gold and shields you — it never heals, and it closes near the end.'),
        rule(icon(ICON.coin), 'Kills pay gold, but only banked gold counts. Survive to the last minute and it is yours.'),
        guideRule()),
      el('div', 'text-[11px] text-[#ffd27a] font-mono mb-2', {}, costEl),
      err,
      btn,
      buyEntriesRow()));

    /**
     * The guide line, with a way back to it for anyone who has already used their
     * first match up. Settings is where the Replay row lives (settings-modal.ts,
     * Game tab), so the deep-link `tab` argument on ctx.openSettings exists exactly
     * for this: sending a player to the fifth row of the first tab they land on is
     * a scavenger hunt, not a shortcut. The button is dropped entirely when the
     * shell hasn't wired openSettings (tests, embeds) rather than faked.
     */
    function guideRule(): Node {
      const line = rule(null, 'FIRST MATCH: a step-by-step guide runs in the arena and points at the rest.');
      if (!ctx.openSettings) return line;
      // Into the text span, not the flex row: a third flex child would open a
      // column of its own and pull the sentence off the shared left edge.
      line.lastElementChild!.append(el('button', 'ml-1 underline decoration-dotted text-[#ffb648] hover:text-[#ffd27a]',
        { title: 'Open Settings → Game → Tutorial', onClick: () => ctx.openSettings?.('game') }, 'Replay it'));
      return line;
    }

    /**
     * "Buy entries" row. The daily budget is server-authoritative (see
     * ArenaBrService.buyEntries); this only shows how much of it is left and posts
     * the purchase, then re-renders the lobby from the fresh status.
     */
    function buyEntriesRow(): Node {
      if (!st) return el('span');
      const row = el('div', 'mt-2 text-[11px] font-mono text-[#e7b58a] flex flex-col items-center gap-1');
      if (boughtLeft <= 0) {
        row.append(el('span', '', {}, `Extra entries bought today: ${st.entriesBought}/${st.maxBoughtPerDay}`));
        return row;
      }
      const buyBtn = el('button', 'arena-btn-ghost px-4 py-1 text-[12px] inline-flex items-center gap-1.5', {},
        '+1 entry · ', icon(ICON.shroom), ` ${entryShrooms}`) as HTMLButtonElement;
      buyBtn.addEventListener('click', async () => {
        if (busy) return; busy = true; buyBtn.disabled = true;
        try {
          // Through the funnel so the purchase carries an actionId: a double-click
          // that slips past `busy` must be deduped by the server, not charged twice.
          await dispatchAction('arena.buyEntries', (id) => arenaBrApi.buyEntries(1, id));
          toast('Extra arena entry bought');
          await ctx.refreshHud();
        } catch (e) {
          if (isDropped(e)) { /* the funnel refused a repeat click — nothing happened */ }
          else toast(e instanceof ApiError ? e.message : 'Could not buy an entry', 'error');
        }
        finally { busy = false; void lobby(); }
      });
      row.append(
        el('span', '', {}, `${left} of ${limit} entries left today · ${boughtLeft} more buyable`),
        buyBtn);
      return row;
    }
  }

  /**
   * A themed yes/no over the arena. Leaving a match throws away a paid entry and any
   * banked gold, so it must never be one stray click — and the game has no shared
   * confirm dialog, hence this local one.
   */
  function confirmLeave(message: string, confirmLabel: string, onYes: () => void) {
    const layer = el('div', 'arena-scene fixed inset-0 z-[65] overflow-hidden grid place-items-center p-3');
    const close = () => layer.remove();
    layer.append(panel(
      el('div', 'text-[17px] tracking-wide text-[#ffb648] mb-1.5', { style: 'text-shadow: 0 2px 0 #7a1f0a' }, 'LEAVE THE ARENA?'),
      el('p', 'text-[11px] leading-snug text-[#e9d6bd]/90 mb-3 px-1', {}, message),
      el('div', 'flex gap-2', {},
        el('button', 'arena-btn-ghost px-4 py-1 text-[12px]', { onClick: close }, 'Keep fighting'),
        el('button', 'arena-btn px-4 py-1 text-[12px]', { onClick: () => { close(); onYes(); } }, confirmLabel))));
    document.body.appendChild(layer);
  }

  // ─── Search: join the real gather lobby, then the offer appears ─────────────
  /**
   * Real matchmaking, not a progress bar. We join the server's open lobby and poll
   * it: every other player who presses Find Match inside the window is seated into
   * the SAME field, taking a slot that would otherwise have been a bot. When the
   * window shuts the field is frozen — humans in the slots they claimed, bots in
   * the rest — and that seating is what the match renders.
   *
   * If the lobby is unreachable the search still works: it falls back to a local
   * seed and a solo seating, which is exactly the old all-bots field. A network
   * blip must not lock a player out of the arena.
   */
  async function search() {
    if (busy) return;
    busy = true;
    clearTimers();
    endSearchGuard();     // a fresh search screen; whatever the last one armed is done
    clear(host);

    let lob: LobbyView | null = null;
    try { lob = await lobbyApi.join(); } catch { lob = null; }

    const youName = ctx.user.username || 'You';
    const fallbackSeed = ((Date.now() & 0x7fffffff) ^ (youName.length * 2654435761)) >>> 0;
    const seed = lob ? lob.seed >>> 0 : fallbackSeed;
    const total = lob?.fieldSize ?? 25;
    const roster = generateRoster(seed, total);   // bot slot i → roster[i] (see buildFighters)
    // Offline fallback keeps the old 1–3 minute wait so the arena still feels queued.
    const target = SEARCH_MIN_MS + Math.floor((((seed >>> 3) % 1000) / 1000) * (SEARCH_MAX_MS - SEARCH_MIN_MS));
    const startedAt = Date.now();
    const closesAt = lob ? lob.closesAt : startedAt + target;

    let seats: MatchSeat[] = lob?.seats?.length ? lob.seats : soloSeats(youName, total);
    let ready = false;
    // Remember WHICH queue this is, so a refresh can rejoin it instead of landing on
    // the lobby with the seat still held server-side. Nothing is charged here — the
    // entry is only spent in startMatch — so a resumed search cannot re-bill anyone.
    if (lob) writeSearchResume({ lobbyId: lob.id, seed, phase: 'search' });
    else clearSearchResume();

    const title = el('div', 'text-[18px] tracking-wide text-[#ffb648] mb-1', { style: 'text-shadow: 0 2px 0 #7a1f0a' }, 'FINDING A MATCH…');
    const sub = el('div', 'text-[11px] text-[#e7b58a] mb-2.5 font-mono', {}, '1/25 warriors gathered');
    const barWrap = el('div', 'w-full h-2 rounded-full bg-black/50 border border-[#7a3a12] overflow-hidden mb-2.5');
    const bar = el('div', 'h-full transition-[width] duration-300', { style: 'width:4%;background:linear-gradient(90deg,#f0842a,#ffd27a)' });
    barWrap.append(bar);
    // Three lines of feed instead of three-and-a-bit: the height is an exact multiple
    // of the line box, so a partially-clipped fourth row never peeks in at the bottom.
    const feed = el('div', 'w-full text-[11px] font-mono text-[#e9d6bd]/80 h-[48px] overflow-hidden mb-2.5 leading-4');
    feed.append(el('div', 'flex items-center justify-center gap-1', {}, icon(ICON.star, 12), `${youName} ready`));
    const cancel = el('button', 'arena-btn-ghost px-5 py-1 text-[12px]',
      // Cancelling the SEARCH is free (no ban) — but the slot has to go back, or the
      // next player would be seated behind a ghost.
      { onClick: () => { clearLeaveGuard(); clearSearchResume(); void lobbyApi.leave().catch(() => undefined); void lobby(); } }, 'Cancel');
    // Leaving the arena TAB mid-search neither forfeits the entry (that is only spent
    // in startMatch below) nor drops the seat: teardown keeps both, and coming back
    // resumes this queue through resumeOrLobby. The guard exists because the search
    // screen stops updating while you are away, not because anything is lost.
    setLeaveGuard('You are searching for a match. Leaving now hides the search — your place is held until the window closes. Leave anyway?');

    host.append(panel(
      el('div', 'arena-spinner mb-3'),
      title, sub, barWrap, feed, cancel));

    const line = (art: HTMLElement, text: string) =>
      feed.prepend(el('div', 'flex items-center justify-center gap-1', {}, art, text));

    // Real players already seated when we joined (we announce ourselves above).
    const announced = new Set<string>([`me:${youName}`]);
    for (const st of seats) {
      if (st.kind === 'human' && st.slot !== (lob?.slot ?? 0)) {
        announced.add(`h:${st.slot}`);
        line(icon(ICON.star, 12), `${st.username || 'A player'} joined`);
      }
    }

    let botsShown = 0;
    const paint = () => {
      const humans = humansIn(seats);
      const t = Math.min(1, 1 - Math.max(0, closesAt - Date.now()) / Math.max(1, closesAt - startedAt));
      // Bots "connect" across the window; humans are counted the moment they seat.
      const wantBots = ready ? total - humans : Math.min(total - humans, Math.round(t * (total - humans)));
      while (botsShown < wantBots) {
        const b = roster[Math.min(roster.length - 1, humans + botsShown)];
        if (b) line(icon(ICON.shroom, 12), `${b.nickname} connected`);
        botsShown++;
      }
      if (botsShown > total - humans) botsShown = total - humans;
      const gathered = Math.min(total, humans + botsShown);
      sub.textContent = humans > 1
        ? `${gathered}/${total} warriors gathered · ${humans} real players`
        : `${gathered}/${total} warriors gathered`;
      bar.style.width = `${Math.round(4 + t * 96)}%`;
    };
    paint();

    // Poll the lobby so a player joining after us visibly takes a bot slot.
    every(2000, () => {
      if (!lob) return;
      void lobbyApi.state().then((st) => {
        if (st.id !== lob!.id) return;   // our lobby closed and a new one opened — ignore
        const before = humansIn(seats);
        seats = st.seats;
        ready = st.ready;
        for (const seat of st.seats) {
          if (seat.kind === 'human' && seat.slot !== st.slot && !announced.has(`h:${seat.slot}`)) {
            announced.add(`h:${seat.slot}`);
            line(icon(ICON.star, 12), `${seat.username || 'A player'} joined`);
          }
        }
        // A human took a bot's slot: one fewer bot has to be shown as connecting.
        const gained = humansIn(seats) - before;
        if (gained > 0) botsShown = Math.max(0, botsShown - gained);
      }).catch(() => undefined);
    });

    every(220, () => {
      paint();
      if (ready || Date.now() >= closesAt) { clearTimers(); endSearchGuard(); offer(seed, seats, lob?.id); }
    });
  }

  /**
   * Hand the seat back without any penalty: the field must not hold a slot for
   * someone who is not going to fight it, and no server route but `decline` bans.
   * Used by every path that ends a queue the player did not refuse — a reload during
   * the offer, and an expired/replaced lobby found on mount.
   */
  function releaseSeat() {
    void lobbyApi.leave().catch(() => undefined);
  }

  /**
   * Give up an offer: free the seat, take the server's search pause, land on the lobby.
   *
   * Shared by Decline and the accept-window timeout — a deliberate refusal and a
   * deliberate silence, which the server treats identically. A RELOAD is NOT one of
   * these and must never reach this function: decline is the only route that sets
   * searchBanUntil (arena-br.service.ts), so routing a refresh through here would fine
   * the player three minutes for pressing F5. Reloads go through resumeOrLobby.
   */
  async function abandonOffer(reason: string) {
    clearSearchResume();
    // Declining also frees the seat.
    releaseSeat();
    try { const r = await arenaBrApi.decline(); toast(`${reason} — search paused briefly`, 'error'); void r; }
    catch { toast(reason, 'error'); }
    void lobby();
  }

  // ─── Offer: an accept window to press Start Battle, else declined ────────────
  /**
   * `endsAt` is the offer's deadline in WALL CLOCK, not a duration, and that is the
   * whole of R2: a reload restores this screen by passing the deadline it was already
   * running against, so the ring picks up at the true remaining seconds instead of
   * restarting a fresh window (which would be free time) or being thrown away (which
   * is what it used to do).
   */
  function offer(seed: number, seats: readonly MatchSeat[], lobbyId?: string, endsAt?: number) {
    clearTimers();
    clear(host);
    const deadline = endsAt ?? Date.now() + acceptWindowMs;
    // Persist the LIVE deadline and the FROZEN seating. The lobby is closed by now, so
    // a restore cannot ask the server who is in this match — it would be told about the
    // next lobby instead.
    if (lobbyId) writeSearchResume({ lobbyId, seed, phase: 'offer', offerEndsAt: deadline, seats: [...seats] });

    const ring = el('div', 'text-[40px] leading-none arena-font text-white mb-2', { style: 'text-shadow: 0 3px 0 #7a1f0a' },
      String(Math.max(0, Math.ceil((deadline - Date.now()) / 1000))));
    const startBtn = el('button',
      'arena-btn px-6 py-1.5 text-base mb-2 inline-flex items-center gap-1.5',
      { onClick: () => void accept() }, icon(ICON.swords, 18), 'Start Battle') as HTMLButtonElement;
    const declineBtn = el('button', 'block mx-auto arena-btn-ghost px-4 py-1 text-[12px]',
      { onClick: () => void declineOffer('You declined the match') }, 'Decline');

    host.append(panel(
      el('div', 'mb-1', {}, icon(ICON.check, 28)),
      el('div', 'text-[18px] tracking-wide text-[#ffb648] mb-1', { style: 'text-shadow: 0 2px 0 #7a1f0a' }, 'MATCH FOUND!'),
      el('div', 'text-[11px] text-[#e7b58a] mb-2 font-mono', {},
        humansIn(seats) > 1
          ? `${seats.length} warriors ready · ${humansIn(seats)} real players · accept to enter`
          : `${seats.length} warriors ready · accept to enter`),
      ring, startBtn, declineBtn));

    every(200, () => {
      const remain = deadline - Date.now();
      ring.textContent = String(Math.max(0, Math.ceil(remain / 1000)));
      if (remain <= 0) void declineOffer('Match missed — you were too slow');
    });

    let taken = false;
    async function accept() {
      if (taken) return; taken = true;
      startBtn.disabled = true; declineBtn.remove();
      clearTimers();
      await startMatch(seed, seats);
    }
    async function declineOffer(reason: string) {
      if (taken) return; taken = true;
      clearTimers();
      await abandonOffer(reason);
    }
  }

  // ─── Enter + mount the match ─────────────────────────────────────────────────
  async function startMatch(seed: number, seats: readonly MatchSeat[]) {
    // THE RECEIPT IS WRITTEN BEFORE THE REQUEST, NOT AFTER.
    //
    // These two lines used to sit below the await, so a reload while `arena.enter`
    // was in flight found the OFFER marker still in storage and no match record:
    // the player was handed the same "Start Battle" button and pressed it again,
    // and the entry that had already been charged was charged a second time. The
    // window is a whole network round trip, on the one call in the game that
    // spends money.
    //
    // Clearing the queue marker first is the same decision from the other side: the
    // moment the entry MIGHT be spent, a refresh must land in the match (rule R3)
    // and never back in a search that looks like a fresh entry.
    //
    // paid/matchId are unknown until the response, so the record goes out with
    // `paid: 0` (understating a charge is the safe direction — the forfeit warning
    // reads "this entry" rather than naming coins that were never taken) and an
    // empty matchId, both patched in below. It carries no fighters, which is what
    // makes matchResumeAction read it as 'restart' and remount the fight on the
    // entry already paid for.
    clearSearchResume();
    const receipt = (paid: number, matchId: string): MatchSnapshot => ({
      v: 2, seed, seats: [...seats], paid, matchId,
      phase: 'lineup', phaseT: 0, phaseDurMs: 0,
      battleClock: 0, deaths: 0, resultSent: false, savedAt: Date.now(),
      heroes: [], combat: [],
    });
    writeMatchSnapshot(receipt(0, ''));

    let paid = 0;
    let matchId = '';
    try {
      // Entry is money: it must carry an actionId so a double-click is deduped by
      // the server instead of being serialised and charged as a second PAID entry.
      // The server is idempotent on the SEED too — a repeated enter() for the same
      // field hands back the open match rather than opening another — so the two
      // layers agree about what a retry means.
      const r = await dispatchAction('arena.enter', (id) => arenaBrApi.enter(seed >>> 0, id));
      paid = r.paid; matchId = r.matchId ?? '';
    } catch (e) {
      // Nothing was entered, so the receipt is a lie — drop it, or the next mount
      // remounts a match on an entry that was never charged. Both failure paths land
      // back on the lobby with the offer over, and the queue marker is already gone.
      clearMatchSnapshot();
      if (isDropped(e)) { void lobby(); return; }   // repeat click — no entry consumed
      const msg = e instanceof ApiError ? e.message : 'Could not enter the arena — check your connection.';
      toast(msg, 'error');
      void lobby();
      return;
    }
    if (paid) { toast(`Paid ${paid} coins entry`); void ctx.refreshHud(); }
    // What was actually charged, and the row every result for this fight must be
    // submitted against.
    writeMatchSnapshot(receipt(paid, matchId));
    mountMatch(seed, seats, paid, matchId, null);
  }

  /**
   * Build the fullscreen match and hand it to the renderer.
   *
   * Split out of startMatch so a RESTORE can reach it WITHOUT going anywhere near
   * `arena.enter`. That is not a nicety: the action-id dedup is per gesture and lives
   * in one request's memory, so a reloaded page mints a fresh id and a second enter()
   * would be a genuine second entry — another counter increment and another 100 coins.
   * The snapshot is the proof the entry was already paid; `paid` comes out of it so
   * the forfeit warning below stays truthful without asking the server, and `matchId`
   * so the restored fight is reported against the row that entry actually opened.
   */
  function mountMatch(seed: number, seats: readonly MatchSeat[], paid: number, matchId: string, restore: MatchSnapshot | null) {
    clear(host);
    const arena = el('div', 'fixed inset-0 z-50 bg-black');
    // Leaving forfeits the entry that was just paid for AND any gold banked this run —
    // it is not refunded and no result is submitted, so it asks first and says so.
    const exit = el('button', 'arena-btn-ghost absolute top-3 left-3 z-10 px-3.5 py-1.5 text-sm',
      { onClick: () => confirmLeave(
        paid ? `You paid ${paid} coins to enter. Leaving now forfeits that entry and any gold you have banked — nothing is refunded.`
          : 'Leaving now forfeits this entry and any gold you have banked — nothing is refunded.',
        'Forfeit & leave',
        () => { toast('You forfeited the match', 'error'); void lobby(); }) },
      '← Leave');
    document.body.appendChild(arena);

    // ── Themed loading screen: warriors load in one-by-one, then the match reveals.
    // A RESTORE is not an entrance — the fight is already on, the player is just
    // getting their eyes back — so it says so and gets out of the way faster.
    const loadName = el('div', 'text-[11px] font-mono text-[#e9d6bd]/85 h-[16px] mb-2', {},
      restore ? 'Rebuilding the battlefield…' : 'Linking warriors…');
    const loadBar = el('div', 'h-full', { style: 'width:6%;background:linear-gradient(90deg,#f0842a,#ffd27a)' });
    const load = el('div', 'arena-scene absolute inset-0 z-20 grid place-items-center p-4 transition-opacity duration-500',
      {},
      panel(
        el('div', 'mb-2 animate-pulse', {}, icon(ICON.swords, 28)),
        el('div', 'text-[18px] tracking-wide text-[#ffb648] mb-1', { style: 'text-shadow: 0 2px 0 #7a1f0a' },
          restore ? 'RECONNECTING TO THE MATCH' : 'ENTERING THE ARENA'),
        el('div', 'text-[11px] text-[#e7b58a] mb-2.5 font-mono', {}, `Loading ${seats.length} warriors…`),
        loadName,
        el('div', 'w-full h-2 rounded-full bg-black/50 border border-[#7a3a12] overflow-hidden', {}, loadBar)));
    arena.append(load);

    // Load list = the seated field: real players by name, bots by their slot's
    // roster name (slot-indexed, same as the match — see buildFighters).
    const roster = generateRoster(seed >>> 0, seats.length);
    const loadNames = seats.map((st, i) =>
      st.kind === 'human' ? (st.username || 'A player') : (roster[st.slot ?? i] ?? roster[roster.length - 1]).nickname);
    const loadStart = Date.now();
    const LOAD_MS = restore ? 900 : 2600;
    every(90, () => {
      const t = Math.min(1, (Date.now() - loadStart) / LOAD_MS);
      loadBar.style.width = `${Math.round(6 + t * 94)}%`;
      const idx = Math.min(loadNames.length - 1, Math.floor(t * loadNames.length));
      const who = loadNames[idx];
      if (who) loadName.textContent = `Loading ${who}… (${idx + 1}/${loadNames.length})`;
      if (t >= 1) { loadName.textContent = 'Ready — enter the arena!'; }
    });

    let matchTeardown: ViewTeardown = () => {};
    try {
      matchTeardown = renderArenaMatch(arena, ctx, seed >>> 0, () => void lobby(), seats, { paid, matchId, restore });
    } catch (e) {
      loadName.textContent = `Arena failed to start: ${e instanceof Error ? e.message : String(e)}`;
    }
    arena.append(exit);
    // Fade the loading screen out once the warriors have "loaded".
    // Both timeouts belong to THIS mount. The second one calls clearTimers(), which
    // clears every view-scoped interval — left running after an early "Leave" it
    // would fire into the lobby and kill the search ticker of the next queue.
    const fadeT = window.setTimeout(() => { load.style.opacity = '0'; }, LOAD_MS);
    const doneT = window.setTimeout(() => { clearTimers(); load.remove(); }, LOAD_MS + 550);
    teardown = () => { window.clearTimeout(fadeT); window.clearTimeout(doneT); matchTeardown(); arena.remove(); };
  }

  /**
   * Give a queue back to a player who reloaded (defect 19).
   *
   * The seat outlives the page — arena-lobby.service.ts seats by userId and join is
   * idempotent — so the only thing lost across a refresh was the client knowing about
   * it. We ask the server what it still holds, and act on `resumeAction`. Resuming
   * goes through the ordinary search(), whose join is the same idempotent call, so a
   * restored search costs exactly what it cost the first time: nothing. The entry
   * (1 free/day, then 100 coins) is charged in startMatch and nowhere else.
   */
  async function resumeOrLobby() {
    // A LIVE MATCH OUTRANKS EVERYTHING (rule R3). It is checked before the queue
    // marker because the two can both exist — the entry is paid while an 'offer'
    // marker is still in storage on some paths — and a paid, half-fought match is
    // never the thing to throw away in favour of re-queuing.
    const snap = readMatchSnapshot();
    const matchAction = matchResumeAction(snap, Date.now());
    if (matchAction === 'resume' && snap) {
      clearSearchResume();
      mountMatch(snap.seed >>> 0, snap.seats ?? [], snap.paid, snap.matchId, snap);
      return;
    }
    if (matchAction === 'restart' && snap) {
      // Paid, but the page went away before the fighters existed. Give back the match
      // that was bought rather than the entry that was lost.
      clearSearchResume();
      if (!snap.matchId) {
        // …except we do not yet know whether it WAS bought: this is the receipt
        // written before the enter request, and the page died before the response
        // named a match. Mounting on it would hand the player a fight the server can
        // never score, so we ask again — safely, because enter() is idempotent on the
        // SEED: the same field either hands back the match already open (entry
        // charged, nothing charged twice) or opens one (it never was). This is the
        // one restore path that may legitimately touch arena.enter, and only because
        // the snapshot carries no proof of payment for it to rely on instead.
        void startMatch(snap.seed >>> 0, snap.seats ?? []);
        return;
      }
      mountMatch(snap.seed >>> 0, snap.seats ?? [], snap.paid, snap.matchId, null);
      return;
    }
    if (matchAction === 'stale') {
      // Reported, finished, or too long ago to hand back. Dropping it here is what
      // stops a snapshot resurrecting a fight that is over.
      clearMatchSnapshot();
      clearSearchResume();
      toast('Your last arena match ended while you were away', 'error');
      void lobby();
      return;
    }

    const saved = readSearchResume();
    if (!saved) { void lobby(); return; }
    let st: LobbyView | null = null;
    if (saved.phase === 'search') {
      // A failed GET says nothing about the seat, and treating it as expiry would
      // recreate the very defect (dropped from a queue the server still holds). The
      // join in search() is idempotent and free, so re-queuing is the safe read.
      try { st = await lobbyApi.state(); }
      catch { clearSearchResume(); void search(); return; }
    }
    const action = resumeAction(saved, st, Date.now());
    clearSearchResume();
    if (action === 'resume-offer') {
      // R2: the offer comes BACK, with what is left of its own deadline, and the
      // player can still take the fight. The seat is not released and — this is the
      // part that used to cost real money — decline() is not called: it is the only
      // route that sets searchBanUntil, and a refresh is not a refusal.
      //
      // The seating comes out of the marker, never off the wire: the lobby closed when
      // the offer was made, so re-fetching would seat a different field under it.
      offer(saved.seed, saved.seats ?? [], saved.lobbyId, saved.offerEndsAt);
      return;
    }
    if (action === 'expired-offer') {
      // The window ran out while the page was gone (or has too little left to press).
      // Still NOT a decline — a page that was not there did not refuse. Hand the seat
      // back and say plainly what happened.
      releaseSeat();
      toast('The match offer expired while you were away — no penalty, search again', 'error');
      void lobby();
      return;
    }
    if (action === 'resume-search') { void search(); return; }
    // Expired: the window shut or the lobby was replaced. The seat can still be ours
    // server-side (ready lobby, replaced lobby), so give it back before leaving or the
    // next player is seated behind a ghost. Say it out loud too — the whole defect was
    // a player being dropped from the queue with no explanation.
    releaseSeat();
    toast('The queue window closed while you were away — search again', 'error');
    void lobby();
  }

  void resumeOrLobby();
  return () => { clearTimers(); endSearchGuard(); teardown?.(); };
}
